import { useField, useFormikContext } from "formik";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { useState, KeyboardEvent } from "react";
import { Input } from "@headlessui/react";
import { CreateRelocationInput } from "@/gql/graphql";
import {
  GenericField,
  InputProps,
} from "@/lib/Components/Form/Fields/GenericField";
import { Badge } from "@/components/catalyst/badge";

interface RelocationReferencesInputProps extends InputProps {
  placeholder?: string;
  max?: number;
}

export function RelocationReferencesInput({
  name,
  label,
  className,
  placeholder = "Type and press Enter or Tab",
  optionalLabel = false,
  max,
  ...props
}: RelocationReferencesInputProps) {
  const { setFieldValue } = useFormikContext<CreateRelocationInput>();
  const [field] = useField<CreateRelocationInput["lines"]>(name);
  const [inputValue, setInputValue] = useState("");

  const upsert = field.value?.upsert || [];
  const currentDeleteIds = field.value?.delete || [];

  function addValue(value: string) {
    const trimmed = value.trim();
    if (!trimmed) return;

    if (max && upsert.length >= max) return;

    // Check if reference already exists
    if (!upsert.some((v) => v.reference === trimmed)) {
      const existingItems = upsert.map(({ id, reference }) => ({
        id,
        reference,
      }));
      setFieldValue(`${name}.upsert`, [
        ...existingItems,
        { reference: trimmed },
      ]);
    }
    setInputValue("");
  }

  function removeValue(index: number) {
    const itemToRemove = upsert[index];
    const newValues = upsert
      .filter((_, i) => i !== index)
      .map(({ id, reference }) => ({ id, reference }));
    setFieldValue(`${name}.upsert`, newValues);

    // If the item has an ID and we're in update mode, add it to the delete array
    if (itemToRemove?.id) {
      const filteredDeleteIds = currentDeleteIds.filter((id) => id != null);
      setFieldValue(`${name}.delete`, [...filteredDeleteIds, itemToRemove.id]);
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      addValue(inputValue);
    } else if (e.key === "Backspace" && !inputValue && upsert.length > 0) {
      removeValue(upsert.length - 1);
    }
  }

  function handleBlur() {
    if (inputValue) {
      addValue(inputValue);
    }
  }

  const viewNode =
    upsert.length === 0 ? (
      <span className="text-gray-500">--</span>
    ) : (
      <div className="flex flex-wrap gap-2">
        {upsert.map((item, index) => (
          <Badge key={index}>{item.reference}</Badge>
        ))}
      </div>
    );

  return (
    <GenericField
      name={name}
      label={label}
      className={className}
      optionalLabel={optionalLabel}
      viewNode={viewNode}
      {...props}
    >
      <div
        data-slot="control"
        className="relative block w-full before:absolute before:inset-px before:rounded-[calc(theme(radius.lg)-1px)] before:bg-white before:shadow-sm after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-inset after:ring-transparent sm:focus-within:after:ring-2 sm:focus-within:after:ring-blue-500"
      >
        <div className="relative flex min-h-[38px] flex-wrap gap-2 rounded-lg border border-zinc-950/10 bg-transparent px-[calc(theme(spacing[3.5])-1px)] py-[calc(theme(spacing[2.5])-1px)] text-base/6 text-zinc-950 hover:border-zinc-950/20 sm:px-[calc(theme(spacing[3])-1px)] sm:py-[calc(theme(spacing[1.5])-1px)] sm:text-sm/6">
          {upsert.map((item, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 text-sm text-blue-700"
            >
              {item.reference}
              <button
                type="button"
                onClick={() => removeValue(index)}
                className="inline-flex h-4 w-4 items-center justify-center rounded-full hover:bg-blue-200"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          ))}
          <Input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            placeholder={upsert.length === 0 ? placeholder : ""}
            className="min-w-[120px] flex-1 border-none bg-transparent p-0 text-base/6 text-zinc-950 outline-none placeholder:text-zinc-500 sm:text-sm/6"
          />
          {max ? (
            <span className="self-start pt-1 text-sm text-zinc-500">
              /{max}
            </span>
          ) : null}
        </div>
      </div>
    </GenericField>
  );
}
